import { and } from 'tio-ui/utilities';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import AppPageProsVsCons from './pros-vs-cons';
import Component from '@glimmer/component';
import Divider from 'tio-ui/components/divider';
import Card from 'tio-ui/components/card';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import { PhCaretDown, PhCaretUp } from 'ember-phosphor-icons';

interface AppPagePlanDetailsCollapsibleSignature {
  Args: {
    title?: string;
    description?: string;
    details: Array<{ title: string; description: string }>;
    pros?: string[];
    cons?: string[];
    moreSections?: Array<{ title: string; description: string }>;
  };
}
// component requires the @details attribute:
// @details must be an array of objects, each with a title (string) and a description (string)
// --All above strings must be translation keys--

// component expects @pros & @cons
// @pros & @cons must be arrays of strings
// --All above strings must be translation keys--
// --If either @pros or @cons is not given, niether will be displayed--

// component can also handle three optional atributes:
// @title must be a string, otherwise will default to "plan details"
// @description must be a string
// @moreSections must be an array of objects, each with a title (string) and a description (string)
// --All above strings must be translation keys--

export default class AppPagePlanDetailsCollapsibleComponent extends Component<AppPagePlanDetailsCollapsibleSignature> {
  @tracked isCollapsed = true;

  toggleCollapsed = () => {
    this.isCollapsed = !this.isCollapsed;
  };

  <template>
    <Card @padding="lg" class="mt-6">
      <Heading @level={{2}} @size="lg" class="mb-4">
        {{if @title (t @title) (t "plan_details.title")}}
      </Heading>
      {{#if @description}}
        <Text @tag="p" @variant="muted" class="mb-4">
          {{t @description htmlSafe=true}}
        </Text>
      {{/if}}
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {{#each @details as |detail|}}
          <div>
            <Text @variant="label" @weight="semibold" class="block mb-1">
              {{t detail.title}}
            </Text>
            <Text @tag="p" @variant="muted" @size="sm">
              {{t detail.description htmlSafe=true}}
            </Text>
          </div>
        {{/each}}
      </div>

      {{#if this.isCollapsed}}
        <div class="tio-flex-end">
          <button
            type="button"
            {{on "click" this.toggleCollapsed}}
            class="tio-row-extra-tight uppercase p-2 tio-fg-brand tio-text-sm-semibold"
          >
            {{t "plan_details.read_more"}}
            <PhCaretDown @size="20" />
          </button>
        </div>

      {{else}}
        {{#if (and @pros @cons)}}
          <Divider class="my-4" />
          <AppPageProsVsCons @pros={{@pros}} @cons={{@cons}} />
        {{/if}}
        {{#if @moreSections}}
          <Divider class="my-4" />
          {{#each @moreSections as |section|}}
            <div class="mt-4">
              <Text @variant="label" @weight="semibold" class="block mb-1">
                {{t section.title}}
              </Text>
              <Text @tag="p" @variant="muted">
                {{t section.description htmlSafe=true}}
              </Text>
            </div>
          {{/each}}
        {{/if}}
        <div class="tio-flex-end">
          <button
            type="button"
            {{on "click" this.toggleCollapsed}}
            class="tio-row-extra-tight uppercase p-2 tio-fg-brand tio-text-sm-semibold"
          >
            {{t "plan_details.read_less"}}
            <PhCaretUp @size="20" />
          </button>
        </div>
      {{/if}}
    </Card>
  </template>
}
